import React, { Component } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as d3 from 'd3';
// import { Transition, TransitionGroup } from 'react-transition-group';

import TreeNode from 'components/TreeNode';
import { setSidePanel, revertSidePanel } from 'reducers/sidePanel/actions';
import {
  RESOURCE_TYPES,
  HIDDEN_TREE_ROOT_ID,
  SIDEPANEL_STATES
} from 'utils/appConstants';

const maxLabelLength = 40;

const Svg = styled.svg`
  background-color: #ffffff;
  border-radius: 4px 4px 0 0;
  flex: 1 1 100%;
  width: 100%;
`;

const SvgFillContainer = styled.div`
  width: 100%;
  flex: 1 1 100%;
  display: flex;
  height: 100%;
`;

const Link = styled.path`
  fill: none;
  stroke: ${({ linkColor }) => linkColor || '#CCC'};
  stroke-width: 1.7px;
`;

const linkColors = [
  '#ED9493',
  '#F3A66C',
  '#E2B762',
  '#CAD263',
  '#2BB893',
  '#0E8392',
  '#046991'
];

class Tree extends Component {
  constructor(props) {
    super(props);

    this.svgRef = React.createRef();
    this.svgGroupRef = React.createRef();
    this.d3tree = d3.tree().size([this.svgHeight]);
    this.longestLabelLength = 10; // default
    this.totalDepth = 0;
    this.horizontalLabelPadding = 7;
    this.verticalNodePadding = 4;

    // TODO: implement/make better
    this.transitionDuration = 0; // ms, default

    this.state = {
      nodeData: [],
      links: [],
      clickedNode: null,
      toggledNodes: []
    };
  }

  /**
   * Set initial settings
   * Generate the initial tree if there are nodes to display.
   */
  componentDidMount() {
    const { onRef, resourceTab, topoNodes, indeNodes, aggregators } =
      this.props;

    if (resourceTab !== RESOURCE_TYPES.TOPONODE) {
      this.verticalNodePadding = 2;
    }

    if (this.svgRef && this.svgRef.current) {
      d3.select(this.svgRef.current).call(this.zoomListener);
    }

    onRef(this);

    if (resourceTab === RESOURCE_TYPES.TOPONODE && topoNodes.length) {
      this.generateTree();
    } else if (resourceTab === RESOURCE_TYPES.INDENODE && indeNodes.length) {
      this.generateTree();
    } else if (
      resourceTab === RESOURCE_TYPES.AGGREGATOR &&
      aggregators.length
    ) {
      this.generateTree();
    }
  }

  /**
   * Check if there has been a change in the redux object that we are
   * generating tree data for. This keeps the page updating and responsive
   * on redux store changes, whether they be user events or back-end events.
   */
  componentDidUpdate(prevProps) {
    const {
      resourceTab,
      indeNodes,
      resourceId,
      topoNodes,
      devices,
      aggregators
    } = this.props;

    if (resourceTab === RESOURCE_TYPES.TOPONODE) {
      if (
        !_.isEqual(prevProps.topoNodes, topoNodes) ||
        !_.isEqual(prevProps.devices, devices)
      ) {
        this.generateTree();
      }
    } else if (resourceTab === RESOURCE_TYPES.INDENODE) {
      if (
        prevProps.resourceId !== resourceId ||
        !_.isEqual(prevProps.devices, devices) ||
        !_.isEqual(prevProps.indeNodes, indeNodes)
      ) {
        this.generateTree();
      }
    } else if (resourceTab === RESOURCE_TYPES.AGGREGATOR) {
      if (
        prevProps.resourceId !== resourceId ||
        !_.isEqual(prevProps.devices, devices) ||
        !_.isEqual(prevProps.aggregators, aggregators)
      ) {
        this.generateTree();
      }
    }
  }

  checkUpdateCondition = ({
    prevResourceId,
    prevDevices,
    prevEntity,
    currentEntity
  }) => {
    const { resourceId, devices } = this.props;
    if (
      prevResourceId !== resourceId ||
      !_.isEqual(prevDevices, devices) ||
      !_.isEqual(prevEntity, currentEntity)
    ) {
      this.generateTree();
    }
  };

  triggerIncrementalZoomIn = () => {
    d3.select(this.svgRef.current).call(this.zoomListener.scaleBy, 1.3);
  };

  triggerIncrementalZoomOut = () => {
    d3.select(this.svgRef.current).call(this.zoomListener.scaleBy, 0.8);
  };

  zoom = () => {
    if (!this.svgGroupRef || !this.svgGroupRef.current) {
      return;
    }

    d3.select(this.svgGroupRef.current).attr('transform', d3.event.transform);
  };

  zoomListener = d3.zoom().scaleExtent([0.1, 3]).on('zoom', this.zoom);

  drawElbowPath = data => {
    if (data.source.children && data.source.children.length > 1) {
      return `M${data.source.y},${data.source.x} h${
        this.longestLabelLength * this.horizontalLabelPadding
      } V${data.target.x} H${data.target.y}`;
    }

    return `M${data.source.y},${data.source.x} V${data.target.x} H${data.target.y}`;
  };
  //TODO: Reconsider primacy line paths. Possibly use non-step
  /*
  drawElbowPath2 = data => {
    return `M${data.source.y},${data.source.x}V${data.target.x}H${
      data.target.y
    }`;
  };

  drawStraightPath(data) {
    const { source, target } = data;
    return `M${source.y},${source.x}L${target.y},${target.x}`;
  }

  drawDiagonalPath(data) {
    const { source, target } = data;
    return linkHorizontal()({
      source: [source.y, source.x],
      target: [target.y, target.x]
    });
  }

  drawStepPath(data) {
    const { source, target } = data;
    const deltaY = target.y - source.y;
    return `M${source.y},${source.x} H${source.y + deltaY / 2} V${target.x} H${
      target.y
    }`;
  }

  */

  /**
   * Toggles the collapsed state of a node's children.
   * @param {object} d - the node information that holds children and collapsed children.
   * We want to flip these two objects (toggle its collapsed state).
   * @return {object} - returned modified object or initial object if no changes made.
   */
  toggleChildren = d => {
    if (d.children) {
      d._collapsedChildren = d.children;
      d.children = null;
      return d;
    } else if (d._collapsedChildren) {
      d.children = d._collapsedChildren;
      d._collapsedChildren = null;
      return d;
    } else if (d.data) {
      if (d.data.children) {
        d.data._collapsedChildren = d.data.children;
        d.data.children = null;
      } else if (d.data._collapsedChildren) {
        d.data.children = d.data._collapsedChildren;
        d.data._collapsedChildren = null;
      }

      return {
        ...d,
        ...d.data,
        x0: d.x,
        y0: d.y
      };
    }
  };

  /**
   * When a user clicks a node, toggle its children collapsed state.
   * Our component's state object holds `toggledNodes` array.
   * When a user clicks a node, add it to the toggle array.
   * If `toggledNodes` already holds the nodeId, then we need to untoggle it.
   * Then, regenerate the tree.
   * @param {object} selectedNode - the full node information when a user clicks on the map
   */
  onNodeClick = async selectedNode => {
    const { toggledNodes } = this.state;
    const { node } = selectedNode;
    if (!toggledNodes.find(modification => modification === node.data.id)) {
      this.setState(
        {
          toggledNodes: [...toggledNodes, node.data.id]
        },
        () => this.generateTree()
      );
    } else {
      const updatedToggledNodes = toggledNodes.filter(
        modification => modification !== node.data.id
      );
      this.setState(
        {
          toggledNodes: updatedToggledNodes
        },
        () => this.generateTree()
      );
    }
    this.setState({ clickedNode: selectedNode });
  };

  /**
   * When a label is clicked, open up the side panel for that node.
   * Determine the type and call the setSidePanel action creator.
   */
  onLabelClick = async nodeData => {
    const {
      setSidePanel,
      resourceTab,
      revertSidePanel,
      sidePanel: { breadcrumbs }
    } = this.props;

    //Find whether a device or toponode was clicked
    const { data } = nodeData.node.data;
    let entity = '';
    let id = '';
    let sfdi = '';
    if (!data.sfdi) {
      entity = RESOURCE_TYPES.TOPONODE;
      id = data.topoNodeId;
    } else {
      entity = RESOURCE_TYPES.DEVICE;
      sfdi = data.sfdi;
    }

    //Check if we're on the indeMap or aggMap pages. If so, we want to keep the previous
    //entity in the breadcrumbs if there is one. So we will revert the sidepanel back to the previous,
    //then immediately set it to the newly chosen entity.
    const keepBreadcrumbs =
      resourceTab === RESOURCE_TYPES.INDENODE ||
      resourceTab === RESOURCE_TYPES.AGGREGATOR;
    if (keepBreadcrumbs && breadcrumbs.length > 0) {
      await revertSidePanel();
    }
    setSidePanel({
      ...(sfdi !== '' ? { sfdi } : { id }),
      entity,
      state: SIDEPANEL_STATES.READ,
      keepBreadcrumbs,
      selectedOnTreeMap: true
    });
  };

  /*
  // For transitions
  linkExit = linkElement => {
    const { clickedNode } = this.state;

    // Transition links to the position of the collapsed node
    if (clickedNode) {
      d3.select(linkElement)
        .transition()
        .duration(this.transitionDuration)
        .attr('d', d => {
          var o = {
            x: clickedNode.x * this.verticalNodePadding,
            y: clickedNode.y
          };
          return this.drawElbowPath({
            source: o,
            target: o
          });
        });
    }
  };

  // For transitions
  nodeExiting = element => {
    const { clickedNode } = this.state;

    // Transition nodes to the position of the collapsed node
    if (clickedNode) {
      d3.select(element)
        .transition()
        .duration(this.transitionDuration)
        .attr('transform', d => {
          return `translate(${clickedNode.y}, ${clickedNode.x *
            this.verticalNodePadding})`;
        });
    }
  };
  */

  hasChildren = node => {
    if (
      (node.children && node.children.length) ||
      (node._collapsedChildren && node._collapsedChildren.length)
    ) {
      return true;
    } else if (node.data) {
      if (
        (node.data.children && node.data.children.length) ||
        (node.data._collapsedChildren && node.data._collapsedChildren.length)
      ) {
        return true;
      }
    }

    return false;
  };

  /*
  // Remove this function after reviewing primacy if we do not need it
  calculateLinkColorRange = totalDepth => {
    let left = 1; // starting depth
    let depthRange = [],
      delta = (totalDepth - left) / (7 - 1);
    while (left < totalDepth) {
      depthRange = [...depthRange, left];
      left += delta;
    }
    depthRange = [...depthRange, totalDepth];
    return depthRange;
  };
  */

  calculateTreeHeight = treeData => {
    let childrenCounts = [1];
    // create an array of the total children lengths for each level
    let childCount = function (level, n) {
      if (n.children && n.children.length > 0) {
        const currLevelCount = childrenCounts[level + 1];
        childrenCounts[level + 1] = currLevelCount
          ? currLevelCount + n.children.length
          : n.children.length;
        n.children.forEach(function (d) {
          childCount(level + 1, d);
        });
      }
    };
    childCount(0, treeData);
    this.totalDepth = childrenCounts.length;
    // d3.max chooses the highest child count in the array
    return d3.max(childrenCounts) * 25; // 25px height padding per line
  };

  /**
   * Takes an ambiguous resource and adds typenames in front of each of its relevant properties.
   * This helps to determine the parent and keeps it unique since there can be multiple
   * nodes with the same ID.
   * @param {objecct} d - the node that the unique resource type IDs should be added to
   * @return {object} Contains all the original data plus the new unique resource IDs
   */
  addUniqueResourceTypeIds = d => {
    d = {
      ...d,
      _deviceId: d.deviceId ? `device_${d.deviceId}` : undefined,
      _topoNodeId: d.topoNodeId ? `topoNode_${d.topoNodeId}` : undefined,
      _indeNodeId: d.indeNodeId ? `indeNode_${d.indeNodeId}` : undefined,
      _aggregatorId: d.aggregatorId
        ? `aggregator_${d.aggregatorId}`
        : undefined,
      _parentTopoNodeId: d.parentTopoNodeId
        ? `topoNode_${d.parentTopoNodeId}`
        : undefined
    };
    return d;
  };

  /**
   * Takes all of the nodes of a resource type, and formats the data into
   * a d3-friendly object.
   * @param {array} treeNodes - list of nodes of a specific resource type
   * @param {string} parentResource - the ID of an indenode or aggregator to format from.
   */
  formatTreeData = (treeNodes, parentResource) => {
    //Use a try block in case some resources have come back from the API but others haven't.
    //For example, if we have devices attached to topoNodes, but topoNodes haven't been fetched yet,
    //an error will occur. We want to wait until the data is available before displaying it.
    //If we catch an error, just return an empty tree object.
    try {
      const { resourceTab } = this.props;

      const hiddenRootNode = {
        _id: HIDDEN_TREE_ROOT_ID,
        parentTopoNodeId: null,
        topoNodeId: HIDDEN_TREE_ROOT_ID
      };
      //If a toponode exists without a parent toponode, set it to HIDDEN_TREE_ROOT_ID
      let treeNodesForFormat = treeNodes.slice();
      if (resourceTab === RESOURCE_TYPES.TOPONODE) {
        treeNodesForFormat = treeNodes.map(node => {
          const newNode = { ...node };
          if (!node.deviceId) {
            newNode.parentTopoNodeId =
              node.parentTopoNodeId || HIDDEN_TREE_ROOT_ID;
          }
          return newNode;
        });
      }

      let treeData = [parentResource || hiddenRootNode, ...treeNodesForFormat];
      let currDepth;

      const rootTreeNode = d3
        .stratify()
        .id(d => {
          d = this.addUniqueResourceTypeIds(d);
          if (d.deviceId) {
            return d._deviceId;
          }
          if (resourceTab === RESOURCE_TYPES.TOPONODE) {
            return d._topoNodeId;
          } else if (resourceTab === RESOURCE_TYPES.INDENODE) {
            return d._indeNodeId;
          } else if (resourceTab === RESOURCE_TYPES.AGGREGATOR) {
            return d._aggregatorId;
          }
        })
        .parentId(d => {
          d = this.addUniqueResourceTypeIds(d);
          if (resourceTab === RESOURCE_TYPES.TOPONODE) {
            if (d.deviceId) {
              return d._topoNodeId;
            } else {
              return d._parentTopoNodeId;
            }
          } else if (resourceTab === RESOURCE_TYPES.INDENODE) {
            if (d.deviceId) {
              return `indeNode_${parentResource.indeNodeId}`;
            }
            return null;
          } else if (resourceTab === RESOURCE_TYPES.AGGREGATOR) {
            if (d.deviceId) {
              return `aggregator_${parentResource.aggregatorId}`;
            }
            return null;
          }
        })(treeData);

      if (resourceTab === RESOURCE_TYPES.TOPONODE) {
        let visitedDepths = [];
        currDepth = rootTreeNode.height;
        rootTreeNode.eachAfter(node => {
          if (
            currDepth !== node.depth &&
            visitedDepths.indexOf(node.depth) < 0
          ) {
            visitedDepths = [...visitedDepths, currDepth];
            currDepth = node.depth;
          } else {
            node._collapsedChildren = node.children;
            node.children = null;
          }
        });
      }

      return rootTreeNode;
    } catch {
      return { children: [] };
    }
  };

  /**
   * Recursively traverse through an array's child elements until we locate the ID.
   * @param {string} nodeId - the ID we are trying to locate the full node information for.
   * @param {array} childrenToTraverse - the array we are traversing while looking for the ID.
   * @return {object || null} - if we find the node, return it. Otherwise return null.
   */
  traverseChildren = (nodeId, childrenToTraverse) => {
    for (let child of childrenToTraverse) {
      if (child.id === nodeId) {
        return child;
      } else {
        const foundNode = this.findChildById(nodeId, child);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  };

  /**
   * Look for an ID in a node's children and _collapsedChildren properties. This will
   * call a recursive function to check through completely.
   * @param {string} nodeId - the ID we are trying to locate the full node data for.
   * @param {object} node - the node we are checking.
   * @return {object || null} - if we find the node, return the object, otherwise null.
   */
  findChildById = (nodeId, node) => {
    if (node.id === nodeId) {
      return node;
    }
    if (node.children) {
      return this.traverseChildren(nodeId, node.children);
    } else if (node._collapsdChildren) {
      return this.traverseChildren(nodeId, node._collapsdChildren);
    }
    return null;
  };

  /**
   * Generate the tree map for the resource type the user is viewing.
   * The data is already in the redux store, we just need to put it together
   * for the d3 package to render, and set it to state to keep beyond re-renders.
   *
   * After a re-render with new data, the tree will generate again.
   * If there were nodes that have been collapsed/uncollapsed by the user, apply
   * those changes (from our state variable `toggledNodes`).
   *
   * Create the nodeData and links, and set them in state.
   */
  generateTree = async () => {
    const {
      resourceTab,
      resourceId,
      topoNodes,
      indeNodes,
      aggregators,
      devices
    } = this.props;

    const { toggledNodes } = this.state;

    //Get the resource type's data from the redux store and set the root in `treeData`
    let treeNodes = [];
    let treeData = {};
    if (resourceTab === RESOURCE_TYPES.TOPONODE) {
      const treeTopoNodes = topoNodes.sort((a, b) => {
        a = a.topoNodeId;
        b = b.topoNodeId;
        return a - b;
      });
      const treeDevices = [];
      devices.forEach(device => {
        if (Array.isArray(device.topoNodeIdList)) {
          for (let i = 0; i < device.topoNodeIdList.length; i++) {
            const treeDevice = { ...device };
            treeDevice.topoNodeId = device.topoNodeIdList[i];
            treeDevices.push(treeDevice);
          }
        }
      });
      treeDevices.sort((a, b) => {
        a = a.deviceId;
        b = b.deviceId;
        return a - b;
      });
      treeNodes = [...treeTopoNodes, ...treeDevices];
      treeData = this.formatTreeData(treeNodes);
    } else {
      if (!resourceId) {
        return this.setState({
          nodeData: [],
          links: []
        });
      }
      if (resourceTab === RESOURCE_TYPES.INDENODE) {
        const parentResource =
          indeNodes.find(indeNode => indeNode.indeNodeId === resourceId) || {};
        const parentResourceId = parentResource.indeNodeId || null;
        const treeDevices = devices.filter(
          device =>
            Array.isArray(device.indeNodeIdList) &&
            device.indeNodeIdList.includes(parentResourceId)
        );
        treeData = this.formatTreeData(treeDevices, parentResource);
      } else if (resourceTab === RESOURCE_TYPES.AGGREGATOR) {
        const parentResource =
          aggregators.find(
            aggregator => aggregator.aggregatorId === resourceId
          ) || {};
        const parentResourceId = parentResource.aggregatorId || null;
        const treeDevices = devices.filter(
          device => device.aggregatorId === parentResourceId
        );
        treeData = this.formatTreeData(treeDevices, parentResource);
      }
    }

    //If there is nothing to display, reset tree to empty and return.
    if (!treeData) {
      this.setState({
        nodeData: [],
        links: []
      });
      return;
    }

    //Check if there are any nodes that have been toggled (collapsed/uncollapsed children).
    //If so, toggle them in this new tree render as well. If they can't be found,
    //it's possible they were deleted in the backend, so delete them from our `toggledNodes` list.
    let updatedToggledNodes = toggledNodes.slice();
    for (let nodeId of toggledNodes) {
      let node = null;
      let parseIndex = 0;
      while (!node && parseIndex < treeData.children.length) {
        node = this.findChildById(nodeId, treeData.children[parseIndex]);
        parseIndex++;
      }
      if (node) {
        this.toggleChildren(node);
      } else {
        updatedToggledNodes = toggledNodes.filter(
          treeModification => treeModification !== nodeId
        );
      }
    }

    //Do all the d3 things to generate the tree.
    const newHeight = this.calculateTreeHeight(treeData);
    this.d3tree = this.d3tree.size([newHeight]);
    this.d3tree = this.d3tree.separation((a, b) => {
      if (a.depth === 1 && b.depth === 1) {
        // force constant padding between top level nodes
        return 2;
      }
      return a.parent.data.id === b.parent.data.id ? 1 : 2; // default
    });

    // Compute the new tree layout.
    let treeRoot = d3.hierarchy(treeData);
    let hierarchy = this.d3tree(treeRoot);

    hierarchy.each(function (node) {
      const ancestorIds = node.ancestors().map(node => node.id);
      node.data._ancestors = ancestorIds;
    });

    let nodes = hierarchy.descendants();
    let links = hierarchy.links();

    // Set widths between levels based on longestLabelLength.
    this.longestLabelLength = nodes.reduce((labelLength, node) => {
      const nodeIdLength = node.data.id ? node.data.id.length : 0;
      const longestLabelLength =
        nodeIdLength > labelLength ? nodeIdLength : labelLength;
      return longestLabelLength > maxLabelLength
        ? maxLabelLength
        : longestLabelLength;
    }, 0);

    const lineLength = this.longestLabelLength * this.horizontalLabelPadding;
    const levelWidth = lineLength * 1.4;

    const primacyRange = [];

    const nodeData = nodes.map((node, i) => {
      const nodeInfo = node.data.data;
      // For the links, we only need to consider nodes that are children.
      // A node with depth of 0 is the HIDDEN_ROOT parent, which has no
      // primacy, and a node with a depth of 1 is a top-level parent,
      // which has no link leading to it, so we can ignore these primacy
      // values so it does not skew our data.
      if (node.depth > 1) {
        if (nodeInfo.hasOwnProperty('primacy')) {
          primacyRange.push(nodeInfo.primacy);
        } else if (nodeInfo.hasOwnProperty('deviceId')) {
          const parentPrimacy = node.parent.data.data.primacy;
          primacyRange.push(parentPrimacy - 1);
        }
      }
      // Subtract line length to move tree left to compensate for hidden root node
      const nodeY = node.depth * levelWidth - lineLength;
      const hasChildren = this.hasChildren(node);
      let nodeName = node.data.id || '';
      nodeName = nodeName.slice(nodeName.indexOf('_') + 1);
      return {
        hasChildren,
        node: {
          ...node,
          y: nodeY ? nodeY : 0,
          attributes: {
            transform: `translate(${nodeY}, ${
              node.x * this.verticalNodePadding
            })` // Add padding between text
          }
        },
        circle: {
          attributes: {
            r: 6
          }
        },
        text: {
          name: nodeName,
          attributes: {
            x: 11,
            dy: '-0.7em',
            textAnchor: 'start'
          }
        }
      };
    });

    // const colorRange = this.calculateLinkColorRange(this.totalDepth);

    const totalColorsAvailable = linkColors.length;
    const uniquePrimacyValues = [...new Set(primacyRange)];
    uniquePrimacyValues.sort((a, b) => a - b);
    const separationWeightValue =
      (uniquePrimacyValues[uniquePrimacyValues.length - 1] -
        uniquePrimacyValues[0]) /
      totalColorsAvailable;
    const linkSpacing = [];
    let unroundedLastValue = uniquePrimacyValues[0];
    for (
      let currColorIndex = 0;
      currColorIndex < totalColorsAvailable;
      currColorIndex++
    ) {
      unroundedLastValue += separationWeightValue;
      linkSpacing.push(Math.round(unroundedLastValue));
    }

    const findMatchingPrimacyIndex = primacy => {
      for (
        let currLinkSpacingIndex = 0;
        currLinkSpacingIndex < linkSpacing.length;
        currLinkSpacingIndex++
      ) {
        const curr = linkSpacing[currLinkSpacingIndex];
        if (primacy <= curr) {
          return Math.max(0, currLinkSpacingIndex);
        }
      }
    };

    links = links.map(link => {
      const targetPrimacy = link.target.data.data.hasOwnProperty('primacy')
        ? link.target.data.data.primacy
        : link.source.data.data.primacy
        ? link.source.data.data.primacy - 1
        : -1;
      const primacyArrayIndex = findMatchingPrimacyIndex(targetPrimacy);
      const linkColor = linkColors[primacyArrayIndex] || '#CCC';
      // TODO: adjust scale for parent nodes with shorter total depths
      // Review this 'depthColor' if needed when reviewing local primacy
      // const depthColor = colorRange.findIndex((rangeStart, i) => {
      //   return i === colorRange.length - 1
      //     ? true
      //     : _.inRange(link.target.depth, colorRange[i]);
      // });
      const linkData = {
        linkColor,
        source: {
          ...link.source,
          y: link.source.depth * levelWidth - lineLength,
          x: link.source.x * this.verticalNodePadding
        },
        target: {
          ...link.target,
          y: link.target.depth * levelWidth - lineLength,
          x: link.target.x * this.verticalNodePadding
        }
      };
      linkData.attributes = {
        d: this.drawElbowPath(linkData)
      };
      return linkData;
    });

    this.setState({
      nodeData,
      links,
      toggledNodes: updatedToggledNodes
    });
  };

  render() {
    const { nodeData, links } = this.state;

    return (
      <SvgFillContainer>
        <Svg ref={this.svgRef}>
          <defs>
            <linearGradient id="nodeGradient" x1="50%" x2="50%" y2="100%">
              <stop stopColor="#11B1D8" />
              <stop offset="1" stopColor="#0779AF" />
            </linearGradient>
          </defs>

          <g ref={this.svgGroupRef}>
            {/* <TransitionGroup component={null}> */}
            {links.map(link =>
              link.source.parent ? (
                // <Transition
                //   key={`link-${link.source.data.id}-${link.target.data.id}`}
                //   timeout={this.transitionDuration}
                //   onExiting={this.linkExit}
                // >
                <Link
                  key={`link-${link.source.data.id}-${link.target.data.id}`}
                  {...link.attributes}
                  linkColor={link.linkColor}
                />
              ) : // </Transition>
              null
            )}
            {nodeData.map(data => {
              // const isSelected =
              //   clickedNode && data.node.data.id === clickedNode.data.id;
              return data.node.parent ? (
                // <Transition
                //   key={`node-${data.node.data.id}`}
                //   timeout={this.transitionDuration}
                //   onExiting={this.nodeExiting}
                // >
                <TreeNode
                  key={`node-${data.node.data.id}-from-${
                    data.node.parent.id || data.node.parent.data.id
                  }`}
                  nodeData={data}
                  isSelected={false}
                  clickHandler={this.onNodeClick}
                  onLabelClick={this.onLabelClick}
                />
              ) : // </Transition>
              null;
            })}
            {/* </TransitionGroup> */}
          </g>
        </Svg>
      </SvgFillContainer>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    topoNodes: state.topoNode.topoNodes,
    sidePanel: state.sidePanel,
    devices: state.device.devices,
    indeNodes: state.indeNode.indeNodes,
    aggregators: state.aggregator.aggregators
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setSidePanel,
      revertSidePanel
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Tree);
